<script setup>
import VEchart from 'vue-echarts';
import 'echarts/lib/chart/line';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/markLine';
import 'echarts/lib/component/dataZoom';
import 'echarts/lib/component/toolbox';
import { computed, ref, defineProps, watch, getCurrentInstance, defineOptions } from 'vue';
import { useChartOptions } from './definition';
import NullWidget from '@/containers/widget/components/NullWidget';
import moment from 'moment/moment';
import ChartLastValue from '../ChartLastValue.vue';
import ChartTitle from '../ChartTitle.vue';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';
import { onMounted, onBeforeUnmount } from 'vue';
import { scpmUtil } from '@/utils/scpmUtil';
const { $util, $theme } = getCurrentInstance().appContext.config.globalProperties;

// Define Name
defineOptions({
  name: 'SummaryChart'
});
//Define props
const props = defineProps({
  summaryData: { type: Array, default: () => [] },
  inquiryTime: { type: Array, default: () => [] },
  options: { type: Object, default: () => ({}) },
  selectedMetric: { type: Object, required: true },
  selectedTargets: { type: [Array, Object], required: true },
  productSubType: { type: String, default: '' },
  productType: { type: String, default: 'VM' },
  collInterval: { type: String, default: '1m' },
  enableLegend: { type: Boolean, default: true },
  parentClass: { type: String, default: '' },
  showTooltipOfChart: { type: Boolean, default: true },
  isNow: { type: Boolean, default: false },
  showLastValue: { type: Boolean, default: () => true }
});

// Define Ref
const chartRef = ref(null);
const chartSeries = ref([]);
const chartType = ref({ type: 'line', stack: null });
const chartActived = ref(false);
const disableHover = ref(false);
const chartKey = ref('');

// Use composable
const { chartOptions, lastValueArr, setXAxisMinMax, setXAxisFormatter, unitConfig } =
  useChartOptions(
    chartSeries,
    chartType,
    {
      lineColor: $theme.COLOR.CHART,
      getPositionTooltip: $util.getPositionTooltip,
      metricUnit: props.options.metricUnit,
      displayUnit: props.options.displayUnit,
      enableLegend: props.enableLegend
    },
    props
  );

const isNullWidget = computed(() => {
  return !props.summaryData.reduce((acc, curr) => [...acc, ...curr.data], []).length;
});

const isShowLastValue = computed(() => {
  if (!props.showLastValue) return false;
  return !isNullWidget.value && lastValueArr.value.length;
});

const initializeChart = () => {
  const [start, end] = props.inquiryTime;
  const [startTime, endTime] = [moment(start).valueOf(), moment(end).valueOf()];
  const { type, stack } = chartType.value;
  setXAxisMinMax(startTime, endTime);
  setXAxisFormatter(endTime - startTime);
  chartSeries.value = props.summaryData.map(({ name, data }) => {
    return {
      name,
      data,
      type,
      stack
    };
  });
  chartKey.value = scpmUtil.generateRandomKey();
};
const handleToogleTitle = (value) => {
  chartActived.value = value;
  legoEventBus.$emit('disable-hover-lastvalue', value);
};
watch(
  () => props.summaryData,
  () => {
    initializeChart();
  },
  { deep: true, immediate: true }
);
onMounted(() => {
  legoEventBus.$on('disable-hover-lastvalue', (isShowInsightChart) => {
    disableHover.value = isShowInsightChart;
  });
});
onBeforeUnmount(() => {
  legoEventBus.$off('disable-hover-lastvalue');
});
</script>
<style scoped lang="scss" src="./style.scss"></style>

<template>
  <div class="chart-container" :class="chartActived && 'show-chart-detail'">
    <div class="chart-header">
      <div class="chart-title" :class="{ 'no-clicked': isNullWidget }">
        <scpm-insight-chart-popup
          v-if="!isNullWidget"
          :refreshKey="chartKey"
          :selected-targets="selectedTargets"
          :selected-metric="selectedMetric"
          :selectedProductType="productType"
          :start="inquiryTime[0]"
          :end="inquiryTime[1]"
          :selectedSubProduct="productSubType"
          :collInterval="collInterval"
          :insightChartOptions="options"
          :parentClass="parentClass"
          :isNow="isNow"
          :showLastValue="showLastValue"
          @toggle="handleToogleTitle"
        >
          <ChartTitle :data="options"></ChartTitle>
        </scpm-insight-chart-popup>

        <ChartTitle maxWidth="255px" v-else :data="options"></ChartTitle>
      </div>
      <ChartLastValue
        v-if="isShowLastValue"
        :last-value-arr="lastValueArr"
        :unitConfig="unitConfig"
        :inquiry-time="inquiryTime"
        :chartRef="chartRef"
        :disableHover="disableHover"
        :showTooltipOfChart="showTooltipOfChart"
        :isSummary="true"
        :isNow="isNow"
      />
    </div>
    <div class="chart-body">
      <div v-if="!isNullWidget" class="chart-contents">
        <v-echart
          :key="chartKey"
          ref="chartRef"
          autoresize
          :option="chartOptions"
          :style="{
            width: `100%`,
            height: `100%`
          }"
        />
      </div>
      <NullWidget v-else />
    </div>
  </div>
</template>
